import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Loader2, ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useOrbVoice } from "@/lib/orbVoice";
import AdelaidaPortrait, { framesForVoice } from "@/components/AdelaidaPortrait";

// Adelaida greets a new member by name, once, straight after sign-up (and on replay from Settings).
export default function AdelaidaWelcome({ onClose, src = "/welcome", eyebrow, playLabel = "Tap to meet" }) {
  const [w, setW] = useState(null);
  const [failed, setFailed] = useState(false);
  const [state, setState] = useState("ready");
  const speakingRef = useRef(false);
  const { user } = useAuth();
  const voice = ["onyx", "echo", "ash", "fable"].includes(user?.voice) ? user.voice : "sage"; // Richard's members meet Richard
  const coach = voice === "sage" ? "Adelaida" : "Richard";
  const { amplitudeRef, speak, stop } = useOrbVoice(voice, "welcome");

  useEffect(() => { api.get(src).then((r) => setW(r.data)).catch(() => setFailed(true)); }, [src]);
  useEffect(() => { if (failed) onClose(); }, [failed]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = async () => {
    setState("speaking"); speakingRef.current = true;
    await speak(w.text, w.audio);
    speakingRef.current = false; setState("done");
  };
  const close = () => { stop(); onClose(); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[120] bg-[#070B14] overflow-y-auto" data-testid="adelaida-welcome">
      <button onClick={close} className="absolute top-5 right-5 z-10 px-3 py-1.5 rounded-full text-xs text-white/60 hover:text-white border border-white/10" data-testid="adelaida-welcome-skip">
        {state === "done" ? "Close" : "Skip"}
      </button>
      <div className="min-h-full flex flex-col items-center px-5 py-10">
        <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-5">{eyebrow || (coach === "Adelaida" ? "Your Head Coach" : "Your Coach")}</p>
        <div className="relative w-full max-w-[320px] aspect-[3/4] rounded-3xl overflow-hidden border border-white/10" data-testid="adelaida-welcome-portrait">
          <AdelaidaPortrait amplitudeRef={amplitudeRef} speakingRef={speakingRef} frames={framesForVoice(voice)} status={state === "speaking" ? "speaking" : "idle"} />
          {state === "ready" && (
            <button onClick={play} disabled={!w} className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/30" data-testid="adelaida-welcome-play" aria-label={`Meet ${coach}`}>
              <span className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-105 transition-transform duration-200">
                {w ? <Play className="w-7 h-7 ml-1" fill="currentColor" /> : <Loader2 className="w-6 h-6 animate-spin" />}
              </span>
              <span className="text-sm text-white/90">{playLabel} {coach}</span>
            </button>
          )}
        </div>
        <div className="max-w-md mt-7 space-y-2.5 text-center" data-testid="adelaida-welcome-text">
          {(w?.lines || []).map((l, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: state === "ready" ? 0.55 : 1, y: 0 }} transition={{ delay: 0.08 * i }}
              className={i >= (w.lines.length - 2) ? "font-serif text-lg italic text-white" : "text-sm text-white/80 leading-relaxed"}>{l}</motion.p>
          ))}
        </div>
        <AnimatePresence>
          {state === "done" && (
            <motion.button initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onClick={close}
              className="mt-8 px-8 py-3.5 rounded-full bg-primary text-primary-foreground font-medium inline-flex items-center gap-2" data-testid="adelaida-welcome-continue">
              Let's begin <ArrowRight className="w-4 h-4" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// Shows the welcome once for a brand-new member, then remembers it on the server.
export function WelcomeGate() {
  const [doneFor, setDoneFor] = useState(null);
  const { user, refreshUser } = useAuth();
  const coachKey = ["onyx", "echo", "ash", "fable"].includes(user?.voice) ? "richard" : "adelaida";
  const done = doneFor === coachKey;
  const { pathname } = useLocation();
  const isPublic = pathname === "/" || /^\/(invite|gift|moment|login|register|forgot-password|reset-password|promo)(\/|$)/.test(pathname);
  const show = !!user && user.welcome_pending && !done && !isPublic;
  if (show) { try { localStorage.setItem(`mirro_welcomed_${user.id}`, "1"); } catch {} }
  const close = () => { setDoneFor(coachKey); api.post("/welcome/heard").then(() => refreshUser?.()).catch(() => {}); };
  return <AnimatePresence>{show && <AdelaidaWelcome key={`aw-${coachKey}`} onClose={close} />}</AnimatePresence>;
}

// First open from the home screen: his own coach thanks him by name, once.
export function InstallWelcomeGate() {
  const [closed, setClosed] = useState(false);
  const { user, refreshUser } = useAuth();
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone || new URLSearchParams(window.location.search).has("installwelcome");
  const show = !!user && standalone && !user.welcome_pending && !user.install_greeted && !closed;
  const close = () => { setClosed(true); api.post("/welcome/install/heard").then(() => refreshUser?.()).catch(() => {}); };
  return <AnimatePresence>{show && <AdelaidaWelcome key="install-welcome" src="/welcome/install" eyebrow="Welcome home" playLabel="Tap to hear" onClose={close} />}</AnimatePresence>;
}
